export interface Author {
  slug: string
  name: string
  age: number
  location: string
  role: string
  bio: string
  /** Short SERP meta description (<=155 chars); the full `bio` runs too long for one. */
  metaDescription: string
  /** Which content pillars/topics this persona is the right byline for. */
  expertise: string[]
  /** Icon glyph used in their abstract badge avatar — see AuthorAvatar.tsx. */
  icon: 'compass' | 'bolt' | 'leaf' | 'jar'
}

// Anonymous field-team personas — see original-savage/notes/Original Savage Authors.md
// in the vault for the full reference and the assignment logic future heartbeat runs
// should use. Preppers generally value OPSEC/anonymity, so these are consistent
// personas with abstract badge avatars, not photos — never invent one beyond what's
// documented here without checking that file first.
export const authors: Author[] = [
  {
    slug: 'arthur-banks',
    name: 'Arthur Banks',
    age: 36,
    location: 'Pacific Northwest, USA',
    role: 'Lifelong Prepper & Field Generalist',
    bio: 'Prepping since 2018, Arthur covers the fundamentals: bug-out bags, navigation, and the everyday skills that matter most before the specialized stuff. Hobbyist first, no formal credentials claimed, just a decade of gear tested in the field.',
    metaDescription: 'Arthur Banks writes Original Savage dispatches on skills, bushcraft, gear and the prepping fundamentals that matter before the specialised kit does.',
    expertise: ['Skills & Bushcraft', 'Gear & Reviews', 'general prepping fundamentals'],
    icon: 'compass',
  },
  {
    slug: 'cameron-spirz',
    name: 'Cameron Spirz',
    age: 37,
    location: 'Eastern Europe',
    role: 'Field Engineer, Power & Off-Grid Systems',
    bio: 'A field engineer by trade, Cameron writes on backup power, generators, batteries, and off-grid electrical systems, the stuff that keeps a home running when the grid goes down.',
    metaDescription: 'Cameron Spirz is a field engineer writing Original Savage dispatches on backup power, generators, batteries and off-grid electrical systems.',
    expertise: ['Home Hardening & Off-Grid Power', 'generators', 'batteries', 'solar'],
    icon: 'bolt',
  },
  {
    slug: 'gordon-nassaur',
    name: 'Gordon Nassaur',
    age: 39,
    location: 'West Coast, USA',
    role: 'Homesteader, Food & Water Self-Sufficiency',
    bio: 'Gordon homesteads on the West Coast, growing his own food and building real household self-sufficiency. He covers water systems, home food production, and the resilience that comes from not depending on a supply chain.',
    metaDescription: 'Gordon Nassaur is a West Coast homesteader writing Original Savage dispatches on water systems, food storage and household self-sufficiency.',
    expertise: ['Food & Water Storage', 'homesteading', 'water systems', 'growing food'],
    icon: 'leaf',
  },
  {
    slug: 'trond-jacobsen',
    name: 'Trond Jacobsen',
    age: 36,
    location: 'Sweden',
    role: 'Electrical Engineer, Homesteading & Food Preservation',
    bio: 'An electrical engineer and homesteader in Sweden, Trond is the all-rounder of the team: food preservation, prepping fundamentals, and the Nordic angle on staying ready through a hard winter.',
    metaDescription: 'Trond Jacobsen is an electrical engineer and homesteader in Sweden, writing Original Savage dispatches on food preservation and hard-winter readiness.',
    expertise: ['Food & Water Storage', 'food preservation', 'electrical systems', 'general prepping'],
    icon: 'jar',
  },
]

export function getAuthor(slug: string): Author {
  const a = authors.find((x) => x.slug === slug)
  if (!a) throw new Error(`Unknown author slug: ${slug}`)
  return a
}
